// src/components/moreoptionsmodal.js
import React, { useState, useEffect } from 'react';
import { FileText } from 'lucide-react';
import InstallationForm from './installationform';
import FormPdfPreview from './formpdfpreview';
import CAForm from './caform';
import ClientReportsModal from './clientreportsmodal';

const MoreOptionsModal = ({ isOpen, onClose, client, user, onHistory, on920Form, onViewEdit, onCreateCustomer, onAddInvoice }) => {
  const [showInstallationForm, setShowInstallationForm] = useState(false);
  const [showCAForm, setShowCAForm] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [showDocumentForm, setShowDocumentForm] = useState(false);

  const [documentIndex, setDocumentIndex] = useState(0);

  useEffect(() => {
    if (!isOpen) return undefined;
    const body = document.body;
    const root = document.documentElement;
    const previousOverflow = body.style.overflow;
    const previousRootOverflow = root.style.overflow;
    body.style.overflow = 'hidden';
    root.style.overflow = 'hidden';
    return () => {
      body.style.overflow = previousOverflow;
      root.style.overflow = previousRootOverflow;
    };
  }, [isOpen]);

  // Reset states when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Small delay to reset after modal closes
      const timer = setTimeout(() => {
        setShowInstallationForm(false);
        setShowCAForm(false);
        setShowReports(false);
        setShowDocumentForm(false);
        setDocumentIndex(0);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen || !client) return null;

  const handleHistory = () => {
    onClose();
    if (onHistory) onHistory(client);
  };

  const handle920Form = () => {
    // Don't call onClose here, just set state
    setShowCAForm(true);
  };

  const handleInstallationForm = () => {
    // Don't call onClose here, just set state
    setShowInstallationForm(true);
  };

  const handleViewEdit = () => {
    onClose();
    if (onViewEdit) onViewEdit(client);
  };

  const handleCreateCustomer = () => {
    onClose();
    if (onCreateCustomer) onCreateCustomer(client);
  };

  const handleAddInvoice = () => {
    onClose();
    if (onAddInvoice) onAddInvoice(client);
  };

  // If installation form is open, show it
  if (showInstallationForm) {
    return (
      <InstallationForm
        clientId={client}
        onSuccess={() => {
          setShowInstallationForm(false);
          onClose();
        }}
        onClose={() => {
          setShowInstallationForm(false);
        }}
      />
    );
  }

const documents = [
  {
    title: 'Customer Lease Agreement',
    url: `https://alcobrakeadmin.com/v3/alcobrake/customerleaseagreement?clientid=${encodeURIComponent(client.id)}#toolbar=0&navpanes=0`,
  },
  {
    title: 'Service Provider',
    url: `https://alcobrakeadmin.com/v3/alcobrake/serviceprovider?clientid=${encodeURIComponent(client.id)}#toolbar=0&navpanes=0`,
  },
  {
    title: 'Standard Fee',
    url: `https://alcobrakeadmin.com/v3/alcobrake/standardfee?clientid=${encodeURIComponent(client.id)}#toolbar=0&navpanes=0`,
  },
  {
    title: 'Customer Checklist',
    url: `https://alcobrakeadmin.com/v3/alcobrake/customerchecklist?clientid=${encodeURIComponent(client.id)}#toolbar=0&navpanes=0`,
  },
  {
    title: 'Installation Service Checklist',
    url: `https://alcobrakeadmin.com/v3/alcobrake/installationchecklist?clientid=${encodeURIComponent(client.id)}#toolbar=0&navpanes=0`,
  },
];

const currentDocument = documents[documentIndex];
if (showDocumentForm) {
  const isFirstDocument = documentIndex === 0;
  const isLastDocument = documentIndex === documents.length - 1;

  return (
    <div className="modal-overlay">
      <div
        className="document-form-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="document-viewer-header">
          <div className="document-header-info">
            <h3>{currentDocument.title}</h3>

            <span className="document-counter">
              Document {documentIndex + 1} of {documents.length}
            </span>
          </div>

          {/* TOP CANCEL */}
          <button
            type="button"
            className="document-cancel-btn"
            onClick={() => {
              setShowDocumentForm(false);
              setDocumentIndex(0);

            }}
          >
            Cancel
          </button>
        </div>

        {/* DOCUMENT */}
        <div className="document-viewer-body">
          <FormPdfPreview
            key={currentDocument.url}
            src={currentDocument.url}
            title={currentDocument.title}
          />
        </div>

        {/* FOOTER */}
        <div className="document-viewer-footer">
          {/* PREVIOUS */}
          <button
            type="button"
            className="document-nav-btn previous"
            disabled={isFirstDocument}
            onClick={() => {

              setDocumentIndex((prev) => prev - 1);
            }}
          >
            ← Previous
          </button>

          {/* CENTER PROGRESS */}
          <div className="document-progress">
            <div className="document-dots">
              {documents.map((doc, index) => (
                <button
                  type="button"
                  key={index}
                  title={doc.title}
                  className={`document-dot ${
                    documentIndex === index ? 'active' : ''
                  }`}
                  onClick={() => {
                    if (index !== documentIndex) {

                      setDocumentIndex(index);
                    }
                  }}
                />
              ))}
            </div>

            <span className="document-progress-text">
              {documentIndex + 1} / {documents.length}
            </span>
          </div>

          {/* NEXT */}
          <button
            type="button"
            className="document-nav-btn next"
            disabled={isLastDocument}
            onClick={() => {

              setDocumentIndex((prev) => prev + 1);
            }}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}

  // If CA Form (920 Form) is open, show it
  if (showCAForm) {
    return (
      <CAForm
        clientId={client}
        onClose={() => {
          setShowCAForm(false);
        }}
      />
    );
  }

  if (showReports) {
    return <ClientReportsModal client={client} onClose={() => setShowReports(false)} />;
  }

  const show920Form = client?.state !== 'INDIA' && client?.state !== 'TX' && client?.state !== 'IL';
  const isIndiaUser = String(user?.state || '').trim().toUpperCase() === 'INDIA';
  const showCreateCustomer = !isIndiaUser && !client?.customer_id;
  const showAddInvoice = !isIndiaUser && Boolean(client?.customer_id);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="more-options-modal" onClick={(e) => e.stopPropagation()}>
        <div className="more-options-header">
          <h3>More Options - {client?.name}</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="more-options-body">
          <div className="options-grid">
            <button className="option-btn reports" onClick={() => setShowReports(true)}>
              <FileText size={17} aria-hidden="true" />
              Reports
            </button>

            <button className="option-btn success" onClick={handleHistory}>
              📋 History
            </button>

            {show920Form && (
              <button className="option-btn primary" onClick={handle920Form}>
                📄 920 Form
              </button>
            )}

            <button className="option-btn dark" onClick={handleInstallationForm}>
              📝 Installation Form
            </button>
            <button
              className="option-btn primary"
              onClick={() => {
                setDocumentIndex(0);

                setShowDocumentForm(true);
              }}
            >
              📑 Document Form
            </button>

            {/* <button className="option-btn orange" onClick={handleViewEdit}>
              ✏️ VIEW/EDIT
            </button> */}

            {showCreateCustomer && (
              <button className="option-btn green" onClick={handleCreateCustomer}>
                👤 Create Customer
              </button>
            )}
            {showAddInvoice && (
              <button className="option-btn red" onClick={handleAddInvoice}>
                💰 Add Invoice
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MoreOptionsModal;
